/**
 * @file
 * 查询 news 接口进行数据拆分
 * 新增可选的 data 查询参数，取值 event / question / news / market
 * 新增 可选的  datas 查询参数，取值 event / question / news / market 逗号分隔的字符串
 * datas优先于data
 *
 * data 值   返回字段
 * news      {"success":true,"data":{"id":"57013","news_id":"0a8b...","title":"..."}}
 * event     {"success":true,"data":{"related_events":[]}}
 * question  {"success":true,"data":{"recommended_questions":[]}}
 * market    {"success":true,"data":{"events":[]}}
 * 空（默认） 全部字段，行为不变
 */
import type { MarketEvent } from '@/components/predic/detail/ImpactMarket';
import useSWR, { SWRConfiguration } from 'swr';

export type MarketNewsDataType = 'news' | 'event' | 'question' | 'market';

export interface MarketNewsBaseData {
  id: string;
  news_id: string;
  title: string;
  summary: string;
  summaryGeneratedAt?: string | null;
  content: string;
  source: string;
  url?: string | null;
  publish_time: string;
  importance_level: number;
  is_breaking: boolean;
  is_hot: boolean;
  category_slug: string | null;
  status: number;
  created_at: string | null;
  updated_at: string | null;
  source_icon_url: string | null;
  source_tier: number | string | null;
  recommended_questions: string[];
}

export interface MarketNewsData extends MarketNewsBaseData {
  events: MarketEvent[];
  related_events: MarketEvent[];
}

export interface MarketNewsQuestionData {
  recommended_questions: string[];
}

export interface MarketNewsMarketData {
  events: MarketEvent[];
}

export interface MarketNewsEventData {
  related_events: MarketEvent[];
}

export type MarketNewsResponseData<T extends MarketNewsDataType | undefined = undefined> =
  T extends 'news'
    ? MarketNewsBaseData
    : T extends 'question'
      ? MarketNewsQuestionData
      : T extends 'market'
        ? MarketNewsMarketData
        : T extends 'event'
          ? MarketNewsEventData
          : MarketNewsData;

export type MarketNewsMultiData = Partial<MarketNewsData>;

export interface MarketNewsResponse<TData = MarketNewsData> {
  success: boolean;
  data: TData;
  error?: string;
  message?: string;
}

export interface UseMarketNewsOptions<T extends MarketNewsDataType | undefined = undefined> {
  newsId?: string | null;
  data?: T;
  datas?: MarketNewsDataType[];
  swr?: SWRConfiguration<MarketNewsResponse<MarketNewsResponseData<T>>, Error>;
}

export interface UseMarketNewsResult<T extends MarketNewsDataType | undefined = undefined> {
  data?: MarketNewsResponse<MarketNewsResponseData<T>>;
  result?: MarketNewsResponseData<T>;
  news?: MarketNewsResponseData<T> extends MarketNewsBaseData
    ? MarketNewsResponseData<T>
    : undefined;
  events: MarketEvent[];
  relatedEvents: MarketEvent[];
  recommendedQuestions: string[];
  error?: Error;
  isLoading: boolean;
  isValidating: boolean;
  refresh: () => void;
}

export function useMarketNews(newsId?: string | null): UseMarketNewsResult;
export function useMarketNews<T extends MarketNewsDataType | undefined = undefined>(
  options?: UseMarketNewsOptions<T>,
): UseMarketNewsResult<T>;
export function useMarketNews<T extends MarketNewsDataType | undefined = undefined>(
  options: string | null | UseMarketNewsOptions<T> = {},
) {
  if (typeof options === 'string' || options === null) {
    return useMarketNewsWithOptions({ newsId: options });
  }

  return useMarketNewsWithOptions(options);
}

function useMarketNewsWithOptions<T extends MarketNewsDataType | undefined = undefined>(
  options: UseMarketNewsOptions<T> = {},
): UseMarketNewsResult<T> {
  const { newsId, data: dataType, datas, swr } = options;
  const searchParams = new URLSearchParams();

  const normalizedDatas = normalizeDataTypes(datas);
  if (normalizedDatas.length > 0) {
    searchParams.set('datas', normalizedDatas.join(','));
  } else if (dataType) {
    searchParams.set('data', dataType);
  }

  const query = searchParams.toString();
  const swrKey = newsId
    ? `api:/market/news/${encodeURIComponent(newsId)}${query ? `?${query}` : ''}`
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    MarketNewsResponse<MarketNewsResponseData<T>>,
    Error
  >(swrKey, {
    revalidateOnFocus: false,
    ...swr,
  });

  const result = data?.success ? data.data : undefined;
  const resultRecord =
    result && typeof result === 'object' ? (result as unknown as Record<string, unknown>) : {};

  const events = Array.isArray(resultRecord.events) ? (resultRecord.events as MarketEvent[]) : [];
  const relatedEvents = Array.isArray(resultRecord.related_events)
    ? (resultRecord.related_events as MarketEvent[])
    : [];
  const recommendedQuestions = Array.isArray(resultRecord.recommended_questions)
    ? (resultRecord.recommended_questions as string[])
    : [];
  const news =
    resultRecord.news_id || resultRecord.title
      ? (result as UseMarketNewsResult<T>['news'])
      : undefined;

  return {
    data,
    result,
    news,
    events,
    relatedEvents,
    recommendedQuestions,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeDataTypes(dataTypes?: MarketNewsDataType[]) {
  if (!dataTypes?.length) return [];
  return [...new Set(dataTypes.filter(Boolean))];
}

export default useMarketNews;
